<!--
  DtfArtCleanupCard -- the Super Admin controls for DTF art cleanup (Plan A,
  Jake 2026-10-29): one switch, default OFF, and the three amounts behind it.

    dtf-art-cleanup-enabled             the Plan A / Plan B switch
    dtf-art-cleanup-fee-usd             fee SHOWN to distributors, DTF cleanup
    dst-digitization-fee-usd            fee SHOWN to distributors, DST
    artistic-punch-cost-per-logo-usd    ThreadFlow's own vendor cost per logo
    dtf-art-cleanup-daily-cap-per-distributor  abuse cap, whole number

  The two fees are disclosure only at MVP (nothing is charged; the cost rides
  in the subscription). The server owns each value in force, its default and
  the allowed range; this card computes none of them. Every amount travels as
  a two-place string ("25.00"), never a float. Reset sends null, which puts
  the default back.

  Switch OFF is the presented state while loading, on a failed read and on a
  malformed answer: a failed read must never show Plan A as on.

  Kept as its own component (like PreviewFallbackToolsCard) so the Platform
  Settings page only mounts it.
-->
<template>
    <div class="dac" data-testid="sa-setting-dtf-art-cleanup">
        <div class="dac-card" data-testid="sa-setting-dtf-art-cleanup-switch-card">
            <div class="dac-card__body">
                <div class="dac-card__copy">
                    <label class="dac-card__label" for="sa-setting-dtf-art-cleanup-switch">
                        DTF art cleanup through Artistic Punch
                    </label>
                    <p class="dac-card__help">
                        When on, a distributor can send DTF art for cleanup
                        from the Send for digitization dialog, after seeing
                        the fee and approving it. When off (the default), DTF
                        art cannot be sent to Artistic Punch, the cleanup
                        options stay hidden, and DTF jobs already queued are
                        left waiting (not submitted) until you turn it back
                        on. While on, a DTF size with a cleanup job still open
                        cannot be approved; a builder can override after 48
                        hours if the vendor never answers. The fees below
                        apply only while this is on.
                    </p>
                    <p
                        class="dac-card__warning"
                        data-testid="sa-setting-dtf-art-cleanup-warning"
                    >
                        Do not turn this on in production until the Artistic
                        Punch return leg for DTF (card #613) is built. Until
                        then a cleanup job never completes, so its size stays
                        unapprovable for 48 hours.
                    </p>
                </div>

                <label class="dac-toggle" data-testid="sa-setting-dtf-art-cleanup-toggle">
                    <input
                        id="sa-setting-dtf-art-cleanup-switch"
                        v-model="enabled"
                        type="checkbox"
                        class="dac-toggle__input"
                        data-testid="sa-setting-dtf-art-cleanup-switch"
                        :disabled="switchSaving"
                        @change="onToggle"
                    />
                    <span class="dac-toggle__track" aria-hidden="true">
                        <span class="dac-toggle__thumb"></span>
                    </span>
                    <span class="dac-toggle__state" data-testid="sa-setting-dtf-art-cleanup-state">{{ enabled ? "On" : "Off" }}</span>
                </label>
            </div>
            <p v-if="switchError" class="dac-card__error" data-testid="sa-setting-dtf-art-cleanup-error">
                {{ switchError }}
            </p>
        </div>

        <div
            v-for="row in rows"
            :key="row.id"
            class="dac-card"
            :data-testid="`sa-setting-${row.id}`"
        >
            <div class="dac-card__copy">
                <label class="dac-card__label" :for="`sa-setting-${row.id}-input`">
                    {{ row.label }}
                </label>
                <p class="dac-card__help" :data-testid="`sa-setting-${row.id}-help`">
                    {{ row.help }}
                </p>
            </div>

            <template v-if="state[row.id].loaded">
                <div class="dac-field">
                    <span
                        v-if="row.kind !== 'int'"
                        class="dac-field__unit"
                        aria-hidden="true"
                    >$</span>
                    <input
                        :id="`sa-setting-${row.id}-input`"
                        v-model="state[row.id].input"
                        type="text"
                        inputmode="decimal"
                        class="dac-field__input"
                        :data-testid="`sa-setting-${row.id}-input`"
                        :disabled="state[row.id].saving"
                    />
                    <p
                        class="dac-card__note"
                        :data-testid="`sa-setting-${row.id}-in-force`"
                        :data-value="state[row.id].server.value"
                    >
                        In force: {{ fmt(row, state[row.id].server.value) }}
                    </p>
                    <p class="dac-card__note" :data-testid="`sa-setting-${row.id}-range`">
                        Allowed {{ fmt(row, state[row.id].server.min) }} to
                        {{ fmt(row, state[row.id].server.max) }}; default
                        {{ fmt(row, state[row.id].server.default) }}.
                    </p>
                </div>
                <div class="dac-actions">
                    <button
                        type="button"
                        class="dac-button"
                        :data-testid="`sa-setting-${row.id}-save`"
                        :disabled="state[row.id].saving"
                        @click="onSave(row)"
                    >
                        Save
                    </button>
                    <button
                        type="button"
                        class="dac-button"
                        :data-testid="`sa-setting-${row.id}-reset`"
                        :disabled="state[row.id].saving"
                        @click="patchAmount(row, null)"
                    >
                        Reset to default
                    </button>
                </div>
            </template>

            <p v-if="state[row.id].saved" class="dac-card__note" :data-testid="`sa-setting-${row.id}-saved`">
                Saved.
            </p>
            <p v-if="state[row.id].error" class="dac-card__error" :data-testid="`sa-setting-${row.id}-error`">
                {{ state[row.id].error }}
            </p>
        </div>
    </div>
</template>

<script setup>
import { onMounted, reactive, ref } from "vue";
import axios from "@/axios";

const BASE = "/api/platform-settings/";
const SWITCH_URL = `${BASE}dtf-art-cleanup-enabled`;

// One row per amount. `id` is the testid stem; `slug` is the API path.
const rows = [
    {
        id: "dtf-art-cleanup-fee",
        slug: "dtf-art-cleanup-fee-usd",
        label: "DTF art cleanup fee shown to distributors",
        help:
            "Shown in the Send for digitization dialog when a distributor " +
            "sends DTF art for cleanup. Disclosure only: nothing is charged " +
            "in the app.",
    },
    {
        id: "dst-digitization-fee",
        slug: "dst-digitization-fee-usd",
        label: "DST digitization fee shown to distributors",
        help:
            "Shown in the Send for digitization dialog when a distributor " +
            "sends art to be digitized for embroidery. Disclosure only: " +
            "nothing is charged in the app.",
    },
    {
        id: "artistic-punch-cost",
        slug: "artistic-punch-cost-per-logo-usd",
        label: "Artistic Punch cost per logo",
        help:
            "What Artistic Punch charges ThreadFlow for one logo. Not shown " +
            "to distributors; recorded on each job when it is created, for " +
            "margin math and the monthly reconciliation.",
    },
    {
        id: "dtf-cleanup-daily-cap",
        slug: "dtf-art-cleanup-daily-cap-per-distributor",
        kind: "int",
        label: "DTF art cleanup daily cap per distributor",
        help:
            "The most DTF art cleanups one distributor can send in a day " +
            "(UTC). Past it a send is refused until tomorrow. An abuse bound " +
            "on vendor cost.",
    },
];

const fmt = (row, v) => (row.kind === "int" ? String(v) : `$${v}`);

// Two places, at most three whole digits: the client's courtesy check. The
// range is the server's, and its sentence is shown verbatim.
const MONEY = /^[0-9]{1,3}(\.[0-9]{1,2})?$/;

const enabled = ref(false);
const switchSaving = ref(false);
const switchError = ref("");

const state = reactive(
    Object.fromEntries(
        rows.map((r) => [
            r.id,
            { loaded: false, server: {}, input: "", saving: false, saved: false, error: "" },
        ])
    )
);

function applyAmount(row, data) {
    const s = state[row.id];
    s.server = data;
    s.input = data.value;
    s.loaded = true;
}

async function loadSwitch() {
    switchError.value = "";
    try {
        const resp = await axios.get(SWITCH_URL);
        // Only a real boolean can show it on.
        enabled.value = resp?.data?.value === true;
    } catch (e) {
        enabled.value = false;
        switchError.value = "Could not load the DTF art cleanup setting.";
    }
}

async function onToggle() {
    const desired = enabled.value;
    switchSaving.value = true;
    switchError.value = "";
    try {
        const resp = await axios.patch(SWITCH_URL, { value: desired });
        enabled.value = resp?.data?.value === true;
    } catch (e) {
        enabled.value = !desired;
        switchError.value =
            e?.response?.status === 403
                ? "Permission denied. Only Super Admins can change this setting."
                : "Could not save this setting.";
    } finally {
        switchSaving.value = false;
    }
}

async function loadAmount(row) {
    const s = state[row.id];
    s.error = "";
    try {
        const resp = await axios.get(BASE + row.slug);
        const data = resp?.data;
        const shapeOk =
            row.kind === "int"
                ? typeof data?.value === "number"
                : typeof data?.value === "string";
        if (!shapeOk) throw new Error("bad shape");
        applyAmount(row, data);
    } catch (e) {
        s.loaded = false;
        s.error = "Could not load this setting.";
    }
}

async function patchAmount(row, value) {
    const s = state[row.id];
    s.saving = true;
    s.error = "";
    s.saved = false;
    try {
        const resp = await axios.patch(BASE + row.slug, { value });
        applyAmount(row, resp?.data);
        s.saved = true;
    } catch (e) {
        const status = e?.response?.status;
        if (status === 403) {
            s.error = "Permission denied. Only Super Admins can change this setting.";
        } else if (status === 422 || status === 400) {
            s.error =
                e?.response?.data?.message ||
                e?.response?.data?.error ||
                "That amount was rejected by the server.";
        } else {
            s.error = "Could not save this setting.";
        }
    } finally {
        s.saving = false;
    }
}

async function onSave(row) {
    const s = state[row.id];
    const raw = String(s.input ?? "").trim();
    if (row.kind === "int") {
        const n = Number(raw);
        if (!/^[0-9]{1,4}$/.test(raw) || n < 1 || n > 1000) {
            s.saved = false;
            s.error = "Enter a whole number from 1 to 1000.";
            return;
        }
        await patchAmount(row, n);
        return;
    }
    if (!MONEY.test(raw)) {
        s.saved = false;
        s.error = "Enter an amount in dollars with at most two decimal places, like 25.00.";
        return;
    }
    await patchAmount(row, raw);
}

onMounted(() => {
    loadSwitch();
    rows.forEach(loadAmount);
});
</script>

<style scoped>
.dac-card {
    background: #fff;
    border: 1px solid #e2e8f0;
    border-radius: 6px;
    padding: 16px 20px;
    margin-bottom: 24px;
}
.dac-card__body {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 24px;
}
.dac-card__copy {
    min-width: 0;
}
.dac-card__label {
    display: block;
    font-weight: 600;
    color: #1f2937;
    font-size: 15px;
    margin-bottom: 4px;
}
.dac-card__help {
    margin: 0;
    color: #4b5563;
    font-size: 13px;
    line-height: 1.5;
    max-width: 460px;
}
.dac-card__warning {
    margin: 8px 0 0;
    padding: 6px 8px;
    border-radius: 4px;
    background: #fff4e0;
    color: #8a5a00;
    font-size: 12px;
    max-width: 460px;
}
.dac-card__note {
    margin: 6px 0 0;
    color: #4b5563;
    font-size: 12px;
}
.dac-card__error {
    margin: 12px 0 0;
    color: #b91c1c;
    font-size: 13px;
}
.dac-field {
    margin-top: 12px;
}
.dac-field__unit {
    margin-right: 4px;
    color: #4b5563;
}
.dac-field__input {
    width: 120px;
    padding: 6px 8px;
    border: 1px solid #cbd2dc;
    border-radius: 4px;
    font-size: 14px;
}
.dac-actions {
    display: flex;
    gap: 8px;
    margin-top: 12px;
}
.dac-button {
    padding: 6px 14px;
    border: 1px solid #cbd2dc;
    border-radius: 4px;
    background: #fff;
    cursor: pointer;
    font-size: 13px;
}
.dac-button:disabled {
    opacity: 0.6;
    cursor: not-allowed;
}
.dac-toggle {
    display: inline-flex;
    align-items: center;
    gap: 10px;
    cursor: pointer;
    flex: 0 0 auto;
}
.dac-toggle__input {
    position: absolute;
    opacity: 0;
    width: 0;
    height: 0;
}
.dac-toggle__track {
    position: relative;
    display: inline-block;
    width: 40px;
    height: 22px;
    border-radius: 999px;
    background: #cbd2dc;
    transition: background 0.15s ease;
    flex: 0 0 auto;
}
.dac-toggle__thumb {
    position: absolute;
    top: 2px;
    left: 2px;
    width: 18px;
    height: 18px;
    border-radius: 50%;
    background: #fff;
    box-shadow: 0 1px 2px rgba(28, 36, 48, 0.25);
    transition: transform 0.15s ease;
}
.dac-toggle__input:checked + .dac-toggle__track {
    background: #2f6df6;
}
.dac-toggle__input:checked + .dac-toggle__track .dac-toggle__thumb {
    transform: translateX(18px);
}
.dac-toggle__input:focus-visible + .dac-toggle__track {
    outline: 2px solid #2f6df6;
    outline-offset: 2px;
}
.dac-toggle__state {
    font-size: 13px;
    color: #5b6572;
}
</style>
